import React, { useState, useEffect } from 'react';
import '../App.css';
import { getApiUrl } from '../apiConfig';

const styles = `
  @keyframes fadeInCenter {
    from { opacity: 0; transform: translateY(12px); }
    to { opacity: 1; transform: translateY(0); }
  }

  .dynamic-center-page {
    animation: fadeInCenter 0.4s ease-out;
  }

  .officers-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
    gap: 32px;
    margin-bottom: 30px;
    justify-items: center;
  }

  @media (min-width: 1200px) {
    .officers-grid {
      grid-template-columns: repeat(4, 1fr) !important;
      gap: 32px !important;
    }
  }

  @media (min-width: 900px) and (max-width: 1199px) {
    .officers-grid {
      grid-template-columns: repeat(3, 1fr) !important;
      gap: 28px !important;
    }
  }

  @media (max-width: 899px) {
    .officers-grid {
      grid-template-columns: repeat(auto-fit, minmax(220px, 1fr)) !important;
      gap: 20px !important;
    }
  }

  .officer-card {
    background-color: #fff;
    border: 2px solid #e8e8e8;
    border-radius: 16px;
    padding: 24px 20px;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    text-align: center;
    width: 100%;
    max-width: 280px;
    display: flex;
    flex-direction: column;
    align-items: center;
    box-sizing: border-box;
  }

  .officer-card:hover {
    transform: translateY(-8px);
    box-shadow: 0 12px 32px rgba(11, 107, 47, 0.25);
    border-color: #0b6b2f;
  }

  .officer-image-wrapper {
    width: 160px;
    height: 180px;
    border-radius: 12px;
    margin-bottom: 18px;
    overflow: hidden;
    border: 3px solid #fff;
    box-shadow: 0 4px 12px rgba(11, 107, 47, 0.2);
    background: #f1f5f9;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .officer-image-wrapper img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

export default function DynamicCenterView({ slug, onBackClick, defaultFallback }) {
  const [centerData, setCenterData] = useState(defaultFallback || null);
  const [loading, setLoading] = useState(!defaultFallback);

  useEffect(() => {
    let isMounted = true;

    const fetchDetails = async () => {
      try {
        const url = getApiUrl(`api_center_details.php?slug=${slug}&t=${Date.now()}`);
        const res = await fetch(url);
        if (!res.ok) throw new Error('API fetch error');
        const data = await res.json();

        if (isMounted && data && data.title) {
          setCenterData(data);
          setLoading(false);
          return;
        }
      } catch (err) {
        console.warn('API details fetch failed, checking local:', err);
      }

      // Fallback to local / public/centers_details.json
      try {
        const fallbackRes = await fetch('/centers_details.json');
        if (fallbackRes.ok) {
          const all = await fallbackRes.json();
          if (isMounted && all && all[slug]) {
            setCenterData(all[slug]);
            setLoading(false);
            return;
          }
        }
      } catch (e) {
        console.warn('Fallback fetch failed:', e);
      }

      if (isMounted && defaultFallback) {
        setCenterData(defaultFallback);
      }
      if (isMounted) setLoading(false);
    };

    fetchDetails();

    return () => {
      isMounted = false;
    };
  }, [slug, defaultFallback]);

  const handleBack = () => {
    if (onBackClick) {
      onBackClick('organization');
    }
  };

  if (loading && !centerData) {
    return (
      <main className="page-content" style={{ padding: '40px 20px', textAlign: 'center' }}>
        <p style={{ color: '#0b6b2f', fontSize: '18px' }}>තොරතුරු ලබා ගනිමින් පවතී...</p>
      </main>
    );
  }

  if (!centerData) {
    return (
      <main className="page-content" style={{ padding: '40px 20px', textAlign: 'center' }}>
        <button onClick={handleBack} style={{ padding: '8px 16px', background: '#0b6b2f', color: '#fff', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>
          ← ආපසු
        </button>
        <p style={{ marginTop: '20px', color: '#666' }}>ආයතන තොරතුරු හමු නොවීය.</p>
      </main>
    );
  }

  const { title, phone, email, officers = [] } = centerData;

  return (
    <main className="page-content dynamic-center-page" style={{ padding: '28px 16px', paddingBottom: '180px', maxWidth: 1200, margin: '0 auto', position: 'relative' }}>
      <style>{styles}</style>

      {/* BACK BUTTON */}
      <button
        className="back-button"
        onClick={handleBack}
        style={{
          backgroundColor: '#0b6b2f',
          color: 'white',
          border: 'none',
          padding: '8px 18px',
          borderRadius: '6px',
          cursor: 'pointer',
          fontSize: '14px',
          marginBottom: '16px',
          display: 'flex',
          alignItems: 'center',
          gap: '6px',
          fontWeight: '600',
          transition: 'all 0.2s',
          boxShadow: '0 2px 8px rgba(11, 107, 47, 0.2)'
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.backgroundColor = '#0a5627';
          e.currentTarget.style.transform = 'scale(1.03)';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.backgroundColor = '#0b6b2f';
          e.currentTarget.style.transform = 'scale(1)';
        }}
      >
        ← ආපසු
      </button>

      {/* TITLE */}
      <h1 style={{ color: '#0b6b2f', fontSize: '28px', marginBottom: '12px', fontWeight: '800' }}>
        {title}
      </h1>
      <hr className="footer-sep" style={{ marginBottom: '20px', opacity: 0.3 }} />

      {/* CONTACT INFORMATION SECTION */}
      {(phone || email) && (
        <div
          className="contact-section"
          style={{
            backgroundColor: '#f8fafc',
            border: '2px solid #0b6b2f',
            borderRadius: '12px',
            padding: '20px 24px',
            marginTop: '20px',
            marginBottom: '35px',
            transition: 'all 0.3s ease',
            boxShadow: '0 4px 12px rgba(0,0,0,0.04)'
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = '#f0fdf4';
            e.currentTarget.style.borderColor = '#0a5627';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = '#f8fafc';
            e.currentTarget.style.borderColor = '#0b6b2f';
          }}
        >
          <h2 style={{
            color: '#0b6b2f',
            fontSize: '18px',
            marginTop: '0',
            marginBottom: '14px',
            fontWeight: '700',
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
          }}>
            📞 සම්බන්ධ තොරතුරු
          </h2>
          {phone && (
            <p style={{ margin: '6px 0', color: '#1e293b', fontSize: '15px' }}>
              <strong>දුරකතන:</strong> <span style={{ color: '#0b6b2f', fontWeight: '600' }}>{phone}</span>
            </p>
          )}
          {email && (
            <p style={{ margin: '6px 0', color: '#1e293b', fontSize: '15px' }}>
              <strong>ඊමේල්:</strong> <span style={{ color: '#0b6b2f', fontWeight: '600' }}>{email}</span>
            </p>
          )}
        </div>
      )}

      {/* OFFICERS SECTION */}
      <div className="officers-section">
        <h2 style={{
          color: '#0b6b2f',
          fontSize: '24px',
          marginBottom: '24px',
          marginTop: '0',
          fontWeight: '800',
          display: 'flex',
          alignItems: 'center',
          gap: '10px'
        }}>
          👥 නිලධාරීන් ({officers.length})
        </h2>

        {officers.length === 0 ? (
          <p style={{ color: '#64748b', textAlign: 'center', padding: '30px', background: '#f8fafc', borderRadius: '12px' }}>
            මෙම ආයතනය සඳහා නිලධාරීන් තවමත් ඇතුළත් කර නොමැත.
          </p>
        ) : (
          <div className="officers-grid">
            {officers.map((officer, index) => (
              <div key={officer.id || index} className="officer-card">
                <div className="officer-image-wrapper">
                  {officer.image ? (
                    <img
                      src={officer.image}
                      alt={officer.name}
                      onError={(e) => {
                        e.target.style.display = 'none';
                        e.target.nextSibling.style.display = 'flex';
                      }}
                    />
                  ) : null}
                  <div
                    style={{
                      width: '100%',
                      height: '100%',
                      display: officer.image ? 'none' : 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '3rem',
                      color: '#94a3b8'
                    }}
                  >
                    👤
                  </div>
                </div>

                <h3 style={{ margin: '0 0 6px 0', fontSize: '1.05rem', color: '#0f172a', fontWeight: '700' }}>
                  {officer.name}
                </h3>
                <p style={{ margin: '0 0 12px 0', fontSize: '0.86rem', color: '#15803d', fontWeight: '600', lineHeight: '1.4' }}>
                  {officer.position}
                </p>
                {officer.phone && (
                  <p style={{ margin: 'auto 0 0 0', fontSize: '0.86rem', color: '#64748b', fontWeight: '500' }}>
                    📞 {officer.phone}
                  </p>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </main>
  );
}
